import moment from 'moment'
import { useDispatch } from 'react-redux'
import * as NotificationsActions from '../../../../actions/notifications'

import DeviceHistory from '../../../../containers/Estate/Store/DeviceHistory'
import config from '../../../../config'
import { colors } from '../../../../constants'
import Confirmation from '../../../Confirmation'
import DeviceIcon from '../../../DeviceIcon'
import ActionLink from './ActionLink'
import Screenshot from './Screenshot'
import { ActionGroup, ChannelsButton, Icon, DeviceIconWrapper, ActionLinkTitle, IdButton, ScreenshotDiv, IdCell } from './Store.styles'
import StatusDot from './StatusDot'
import { Input } from '../../../../components'
import DeviceWarning from '../../../DeviceWarning'
import DeviceDeletion from './DeviceDeletion'

const getMoment = (date) => (date ? moment(date).fromNow() : '')

const getMixedScreenshotCondition = (device_type, store_location) => {
    return device_type === 'TIZEN' && store_location.name === 'ISPC_VISION'
}

const hasAbilityToTakeScreenshot = (device_type, store_location) => {
    const deviceTypesNoScreenshot = ['SERVER', 'WATCH']
    const storeLocationsNoScreenshot = ['ISPC_VISION_CDD', 'ARK_WATCH']
    return !(
        deviceTypesNoScreenshot.includes(device_type) ||
        storeLocationsNoScreenshot.includes(store_location.name) ||
        getMixedScreenshotCondition(device_type, store_location)
    )
}

const CopyIdIcon = ({ id, t }) => {
    
    const dispatch = useDispatch()

    const handleCopy = (e) => {
        if (typeof e?.stopPropagation === 'function') e.stopPropagation()
        const text = String(id)
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(text)
        } else {
            const el = document.createElement('textarea')
            el.value = text
            document.body.appendChild(el)
            el.select()
            try {
                document.execCommand('copy')
            } catch {}
            document.body.removeChild(el)
        }

        if (typeof dispatch === 'function') {
            dispatch(
                NotificationsActions.notify({
                    title: t ? t('Copied') : 'Copied',
                    body: text,
                })
            )
        }
    }

    return <Icon icon="clipboard-list" title={t ? t('Copy ID') : 'Copy ID'} color={colors.white} onClick={handleCopy} style={{ cursor: 'pointer' }} />
}

const deviceTableDisplay = ({
    hasPermissionToChangeConfig,
    hasPermissionToPushContent,
    hasPermissionToReboot,
    onClickChannels,
    onOpenDeviceUpdateForm,
    openCloseModal,
    socket,
    t,
    bulkSelectionDevices,
    bulkSelectionAllDevices,
    selectedAll,
    reloadStore,
}) => [
    {
        field: null,
        label: null,
        key: 'device_checkbox',
        props: { align: 'left' },
        // eslint-disable-next-line react/prop-types
        transform: ({ selected, id }) => (
            <Input type="check" value={selected} onChange={(e) => bulkSelectionDevices(e, id)} borderColor={colors.bluedarkest} isInRow>
                {t('View')}
            </Input>
        ),
        header: (
            <Input type="check" value={selectedAll} onChange={(e) => bulkSelectionAllDevices(e)} borderColor={colors.bluedarkest} isInRow>
                {t('View')}
            </Input>
        ),
        selectable: 'mandatory',
        width: '35px',
    },
    {
        onlyOnMobile: true,
        field: 'id',
        key: 'id',
        label: 'ID',
        props: { align: 'left' },
        transform: (id) => (
            <IdCell>
                <IdButton
                    onClick={() => {
                        if (hasPermissionToChangeConfig) onOpenDeviceUpdateForm(id)
                    }}
                >
                    {id}
                </IdButton>
                <CopyIdIcon id={id} t={t} />
            </IdCell>
        ),
        width: '135px',
    },
    {
        onlyOnMobile: true,
        field: 'store_location',
        key: 'store_location',
        label: t('Location'),
        props: null,
        transform: (sl) => sl.name,
        width: '150px',
    },
    {
        onlyOnMobile: true,
        field: 'isOnline',
        key: 'is_online',
        transform: StatusDot,
        width: '10px',
    },
    {
        field: 'device_type',
        key: 'device_type',
        label: t('Type'),
        props: { backgroundColor: 'white' },
        transform: (type) => (
            <DeviceIconWrapper>
                <DeviceIcon code={type} />
            </DeviceIconWrapper>
        ),
        width: '70px',
    },
    {
        field: 'id',
        label: 'ID',
        key: 'id',
        props: null,
        transform: (id) => (
            <IdCell>
                <IdButton
                    noBorder
                    onClick={() => {
                        if (hasPermissionToChangeConfig) onOpenDeviceUpdateForm(id)
                    }}
                >
                    {id}
                </IdButton>
                <CopyIdIcon id={id} t={t} />
            </IdCell>
        ),
        width: '135px',
    },
    {
        field: null,
        label: '',
        key: 'screenshot',
        name: t('screenshot'),
        props: { align: 'center' },
        transform: ({ screenshotSrc, screenshotHighResSrc }) => {
            return (
                <ScreenshotDiv>
                    <Screenshot src={screenshotSrc} srcHighRes={screenshotHighResSrc} />
                </ScreenshotDiv>
            )
        },
        width: '85px',
        class: 'screenshot',
    },
    {
        field: 'store_location',
        label: t('Location'),
        key: 'store_location',
        props: null,
        transform: (sl) => sl.name,
        width: '250px',
    },
    {
        field: null,
        label: t('Status'),
        key: 'device_status',
        props: { align: 'center' },
        transform: (device) => <StatusDot socket={socket} isOnline={device.isOnline} last_contact={device.last_contact} />,
        width: '80px',
    },
    {
        field: 'lastContact',
        label: t('Contacted'),
        key: 'last_contact',
        selectable: 'hidden',
        transform: (lc) => lc && getMoment(lc),
        width: '100px',
    },
    {
        field: 'lastDownload',
        label: t('Downloaded'),
        key: 'last_download',
        transform: (ld) => (typeof ld === 'string' ? ld : getMoment(ld)),
        width: '130px',
    },
    {
        field: 'sku',
        label: t('sku'),
        key: 'sku',
        onlyOnLG: true,
        props: { align: 'left' },
        width: '70px',
    },
    {
        field: 'manufacturer',
        label: t('manufacturer'),
        key: 'manufacturer',
        onlyOnLG: true,
        props: { align: 'left' },
        width: '170px',
    },
    {
        field: 'model',
        label: t('model'),
        key: 'model',
        onlyOnLG: true,
        props: { align: 'left' },
        width: '130px',
    },
    {
        field: null,
        label: t('Channels'),
        key: 'channels',
        transform: (
            { id, channels } // eslint-disable-line react/prop-types
        ) => <ChannelsButton onClick={() => onClickChannels(id)}>{channels.length}</ChannelsButton>,
        width: '90px',
        props: { align: 'center' },
    },
    {
        field: 'screen_rotation',
        label: t('device-orientation'),
        key: 'screen_rotation',
        width: '100px',
    },
    {
        field: null,
        label: '',
        key: 'hdmi_status',
        name: t('device-status-health'),
        props: { align: 'center' },
        transform: (device) => {
            return <DeviceWarning device={device} />
        },
        width: '50px',
    },
    {
        field: 'current_apk',
        label: t('device-status-apk-version'),
        key: 'current_apk',
        props: { align: 'center' },
        width: '100px',
    },
    {
        field: 'wired',
        label: t('connection'),
        key: 'wired',
        name: t('connection'),
        props: { align: 'center' },
        transform: (wired) => {
            const isValidType = typeof wired === 'boolean' || typeof wired === 'number' || wired === null
            if (isValidType) {
                if (wired === null) {
                    return null
                }

                if (wired) {
                    return <Icon icon="ethernet" color={colors.black} title={t('device.wired.ethernet')} />
                }

                return <Icon icon="wifi" color={colors.black} title={t('device.wired.wifi')} />
            }
        },
        width: '100px',
    },
    {
        field: 'disable_zoom',
        label: t('disable zoom'),
        key: 'disable_zoom',
        name: t('disable zoom'),
        props: { align: 'center' },
        width: '100px',
    },
    {
        field: '4k_resolution',
        label: t('4K'),
        key: 'four_k',
        name: t('4K'),
        props: { align: 'center' },
        width: '100px',
    },
    {
        field: 'filter_level',
        label: t('filter level'),
        key: 'filter_level',
        name: t('filter level'),
        props: { align: 'center' },
        width: '100px',
    },
    {
        field: 'antenna_level',
        label: t('antenna level'),
        key: 'antenna_level',
        name: t('antenna level'),
        props: { align: 'center' },
        width: '100px',
    },
    {
        field: null,
        key: 'actions',
        label: '',
        props: { align: 'right' },
        selectable: 'mandatory',
        // eslint-disable-next-line react/prop-types
        transform: ({ id, expires_at, device_type, store_location }) => {
            if (expires_at && new Date(expires_at) < new Date()) {
                return (
                    <ActionGroup>
                        <ActionLink noCursor>
                            <Icon icon="calendar-times" title={t('Expired license')} color={colors.red} />
                            <ActionLinkTitle>{t('Expired license')}</ActionLinkTitle>
                        </ActionLink>
                    </ActionGroup>
                )
            }

            return (
                <ActionGroup>
                    <DeviceDeletion deviceId={id} reloadStore={reloadStore} />
                    {hasPermissionToReboot && (
                        <ActionLink
                            onClick={(e) => {
                                if (typeof e?.preventDefault === 'function') {
                                    e.preventDefault()
                                }

                                if (socket) {
                                    socket.rebootDevice(id)
                                }
                            }}
                            confirmation={`${t('RebootConfirmation')} ${id}`}
                            openConfirmation={openCloseModal}
                            confirmationContent={`${t('RebootConfirmBody')} "${id}"?`}
                            confirmationHeader={t('RebootConfirmHeader')}
                        >
                            <Icon icon="bolt" title="Reboot" color={colors.white} />
                        </ActionLink>
                    )}
                    {hasPermissionToPushContent && (
                        <ActionLink
                            onClick={(e) => {
                                if (typeof e?.preventDefault === 'function') {
                                    e.preventDefault()
                                }

                                if (socket) {
                                    socket.updateContent(id)
                                }
                            }}
                            confirmation={`${t('UpdateContentConfirmation')} ${id}`}
                            openConfirmation={openCloseModal}
                            confirmationContent={`${t('UpdateContentConfirmBody')} ${id} ${t('UpdateContentConfirmBodySecond')}?`}
                            confirmationHeader={t('UpdateContentConfirmHeader')}
                        >
                            <Icon icon="cloud-download-alt" title="Update Content" color={colors.white} />
                        </ActionLink>
                    )}
                    {hasPermissionToChangeConfig && (
                        <ActionLink
                            onClick={() => {
                                onOpenDeviceUpdateForm(id)
                            }}
                        >
                            <Icon icon="wrench" title="Update Settings" color={colors.white} />
                        </ActionLink>
                    )}
                    {hasAbilityToTakeScreenshot(device_type, store_location) && (
                        <ActionLink
                            onClick={(e) => {
                                if (typeof e?.preventDefault === 'function') {
                                    e.preventDefault()
                                }

                                if (socket) {
                                    socket.demandScreenshot(id)
                                }
                            }}
                            confirmation={t('ScreenShotConfirmation')}
                            confirmationTimeout={config.screenshots.timeToRefresh}
                        >
                            <Icon icon="image" title="Screenshot" color={colors.white} />
                        </ActionLink>
                    )}
                    <ActionLink
                        onClick={(e) => {
                            if (typeof e?.preventDefault === 'function') {
                                e.preventDefault()
                            }

                            if (socket) {
                                socket.openStatusPanel(id)
                            }
                        }}
                        confirmation={t('OpenStatusPanelConfirmation')}
                    >
                        <Icon icon="bars" title="Open Status Panel" color={colors.white} />
                    </ActionLink>
                    {hasPermissionToChangeConfig && (
                        <ActionLink noLink>
                            <Confirmation confirmation={<DeviceHistory device={id} />} header={t('device-history')}>
                                <Icon icon="history" title="Device History" color={colors.white} />
                            </Confirmation>
                        </ActionLink>
                    )}
                    <ActionLink
                        onClick={(e) => {
                            if (typeof e?.preventDefault === 'function') {
                                e.preventDefault()
                            }

                            if (socket) {
                                socket.restartApp(id)
                            }
                        }}
                        confirmation={`${t('RestartConfirmation')} ${id}`}
                        openConfirmation={openCloseModal}
                        confirmationContent={`${t('RestartConfirmBody')} "${id}"?`}
                        confirmationHeader={t('RestartConfirmHeader')}
                    >
                        <Icon icon="redo" title="Restart" color={colors.white} />
                    </ActionLink>
                </ActionGroup>
            )
        },
        width: null,
    },
]

export default deviceTableDisplay
